"use client";

import ReactMarkdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

/**
 * Same plugin pair as `SourceChip`: GFM for tables/strikethrough/autolinks and
 * `remark-breaks` so a single newline in model prose reads as a line break
 * (models write chat text, not CommonMark paragraphs).
 *
 * Deliberately NO `rehype-raw`: the content here is model- and user-authored,
 * so react-markdown's default HTML escaping is the security boundary and must
 * stay on.
 */
const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

/* -------------------------------------------------------------------------- */
/* Element styling                                                            */
/* -------------------------------------------------------------------------- */

/**
 * `tokens.css` styles `p`, `pre`, `code`, `h1` and `h2` **unlayered**, so those
 * bare-element rules beat Tailwind's layered utilities. Every utility that has
 * to win against them carries the v4 trailing `!`.
 */
const COMPONENTS: Components = {
  p: ({ children }) => (
    <p className="text-[14px]! leading-[1.55]! text-(--fg-primary)!">
      {children}
    </p>
  ),

  // Tables are the reason this component exists: the agent presents its
  // assumption ledger as a GFM table. A wide table scrolls inside its own box
  // rather than widening the chat column.
  table: ({ children }) => (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full border-collapse text-left text-[13px] leading-[1.45]">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="whitespace-nowrap border-b border-(--border-default) px-2 py-1.5 text-left align-bottom font-medium text-(--fg-secondary)">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-(--border-subtle) px-2 py-1.5 align-top text-(--fg-primary)">
      {children}
    </td>
  ),

  ul: ({ children }) => (
    <ul className="flex list-disc flex-col gap-1 pl-5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="flex list-decimal flex-col gap-1 pl-5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-[14px] leading-[1.55] text-(--fg-primary)">
      {children}
    </li>
  ),

  h1: ({ children }) => (
    <h1 className="text-[15px]! leading-[20px]! text-(--fg-primary)!">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[14px]! leading-[19px]! text-(--fg-primary)!">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[13px] leading-[18px] text-(--fg-primary)">
      {children}
    </h3>
  ),

  strong: ({ children }) => (
    <strong className="font-bold text-(--fg-primary)">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,

  code: ({ children }) => (
    <code className="rounded-(--radius-xs)! bg-(--bg-canvas)! px-[5px]! py-[2px]! font-(family-name:--font-mono)! text-[0.9em]!">
      {children}
    </code>
  ),
  // `pre code` must not double up the inline chip styling above; `!` is what
  // beats both the utility and the unlayered `pre code` rule in tokens.css.
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-(--radius-sm)! bg-(--bg-canvas)! px-[14px]! py-[12px]! text-[12px]! [&_code]:bg-transparent! [&_code]:p-0!">
      {children}
    </pre>
  ),

  blockquote: ({ children }) => (
    <blockquote className="flex flex-col gap-2 border-(--border-default) border-l-2 pl-3 text-(--fg-secondary)">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a className="break-words" href={href} rel="noreferrer" target="_blank">
      {children}
    </a>
  ),
  hr: () => <hr className="my-1 border-(--border-subtle) border-t" />,
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export interface MarkdownProseProps {
  /** Raw markdown. Rendered escaped — embedded HTML is never interpreted. */
  children: string;
  /** Extra classes on the wrapper (block spacing lives here). */
  className?: string;
}

/**
 * Renders agent/model markdown as real prose: GFM tables, lists, emphasis and
 * code, all styled from design tokens only.
 *
 * Use this anywhere a chat transcript shows model-authored text. The intake
 * wizard's transcript still renders `whitespace-pre-wrap` plain text and has
 * the same latent problem, which is why this lives in the shared package.
 */
export function MarkdownProse({ children, className }: MarkdownProseProps) {
  return (
    <div
      className={["flex min-w-0 flex-col gap-3", className]
        .filter(Boolean)
        .join(" ")}
    >
      <ReactMarkdown components={COMPONENTS} remarkPlugins={REMARK_PLUGINS}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
